import { Column, Table } from "@tanstack/react-table";
import React, { useEffect } from "react";
import { keyBy } from "lodash";
import { useQueryState } from "../use-query-state";

export interface FilteredColumnDef {
    filter?: ({ column, table }: { column: Column<any, any, unknown>; table: Table<any, any> }) => React.ReactElement;
}

// https://tanstack.com/table/v8/docs/examples/react/filters

export const HeaderFilter = ({ column, header, filter }) => {
    column.columnDef.header = header;
    column.columnDef.filter = filter;
    return header;
};

export const Filter = ({ column, table }): React.ReactElement => {
    return <div className="reacttable-filter">{column.columnDef.filter && column.columnDef.filter({ column, table })}</div>;
};

export const DefaultColumnFilter = ({ column }: { column: Column<any, any, unknown>; table: Table<any, any> }) => {
    const filterValue: any = column.getFilterValue();
    const [queryValue, setQueryValue] = useQueryState(column.id, filterValue || "");
    column.columnDef.filterFn = "includesString";

    useEffect(() => {
        if (queryValue !== filterValue && queryValue !== "") {
            column.setFilterValue(queryValue);
        }
    }, [filterValue, queryValue, column]);

    return (
        <input
            type="text"
            className="form-input"
            value={filterValue || ""}
            onChange={(e) => {
                column.setFilterValue(e.target.value);
                setQueryValue(e.target.value || "");
            }}
            size={column.columnDef.maxSize || null}
        />
    );
};

export const SelectColumnFilter = ({ column }: { column: Column<any, any, unknown>; table: Table<any, any> }) => {
    const filterValue: any = column.getFilterValue();
    const [queryValue, setQueryValue] = useQueryState(column.id, filterValue || "");
    const options = React.useMemo(() => Array.from(column.getFacetedUniqueValues().keys()).sort(), [column]);
    column.columnDef.filterFn = "equals";

    useEffect(() => {
        if (queryValue !== filterValue && queryValue !== "") {
            column.setFilterValue(queryValue);
        }
    }, [filterValue, queryValue, column]);

    return (
        <select
            id="custom-select"
            className="custom-select form-select"
            value={filterValue}
            onChange={(e) => {
                column.setFilterValue(e.target.value);
                setQueryValue(e.target.value || "");
            }}
        >
            <option value="">All</option>
            {options.map((option: any) => (
                <option key={"key" + option} value={option}>
                    {option}
                </option>
            ))}
        </select>
    );
};

//Closure
export const SelectColumnFilterType = (enumType) => {
    const SelectColumnFilterComponent = ({ enumType, column }) => {
        const filterValue: any = column.getFilterValue();
        const [queryValue, setQueryValue] = useQueryState(column.id, filterValue || "");

        const options: string[] = Object.values(enumType);
        column.columnDef.filterFn = "equals";

        useEffect(() => {
            if (queryValue !== filterValue && queryValue !== "") {
                column.setFilterValue(queryValue);
            }
        }, [filterValue, queryValue, column]);

        return (
            <select
                id="custom-select"
                className="custom-select"
                value={filterValue}
                onChange={(e) => {
                    column.setFilterValue(e.target.value || undefined);
                    setQueryValue(e.target.value || "");
                }}
            >
                <option value="">All</option>
                {options.map((option: any) => (
                    <option key={option} value={option}>
                        {option}
                    </option>
                ))}
            </select>
        );
    };

    const FilterComponent = ({ column }: { column: Column<any, any, unknown>; table: Table<any, any> }): React.ReactElement => {
        return <SelectColumnFilterComponent enumType={enumType} column={column} />;
    };
    (FilterComponent as any).filterFn = "equals";
    return FilterComponent;
};

export const SelectNotZeroColumnFilter = ({ column }: { column: Column<any, any, unknown>; table: Table<any, any> }) => {
    const filterValue: any = column.getFilterValue();
    const [queryValue, setQueryValue] = useQueryState(column.id, filterValue || "");

    (column.columnDef as any).filterFn = "zeroFilter";

    useEffect(() => {
        if (queryValue !== filterValue && queryValue !== "") {
            column.setFilterValue(queryValue);
        }
    }, [filterValue, queryValue, column]);

    return (
        <select
            id="custom-select"
            className="custom-select"
            value={filterValue}
            onChange={(e) => {
                column.setFilterValue(e.target.value);
                setQueryValue(e.target.value || "");
            }}
        >
            <option value="">All</option>
            <option value="0">Exclude 0</option>
            <option value="only0">Only 0</option>
        </select>
    );
};

//Closure
export const SelectColumnFilterArrayOfIds = (lookup: { _id: string; name: string }[]) => {
    const SelectColumnFilterComponent = ({ lookup, column }) => {
        const filterValue: any = column.getFilterValue();
        const [queryValue, setQueryValue] = useQueryState(column.id, filterValue || "");

        useEffect(() => {
            if (queryValue !== filterValue && queryValue !== "") {
                column.setFilterValue(queryValue);
            }
        }, [filterValue, queryValue, column]);

        const byName = keyBy(lookup, "name");
        const optionNames = Object.keys(byName).sort();

        return (
            <select
                id="custom-select"
                className="custom-select"
                value={filterValue}
                onChange={(e) => {
                    column.setFilterValue(e.target.value || undefined);
                    setQueryValue(e.target.value || "");
                }}
            >
                <option value="">All</option>
                {optionNames.map((optionName: string) => (
                    <option key={optionName} value={byName[optionName]._id}>
                        {optionName}
                    </option>
                ))}
            </select>
        );
    };

    const FilterComponent = ({ column }: { column: Column<any, any, unknown>; table: Table<any, any> }): React.ReactElement => {
        if (!column.columnDef.filterFn || column.columnDef.filterFn === "auto") {
            column.columnDef.filterFn = "arrIncludes";
        }
        return <SelectColumnFilterComponent lookup={lookup} column={column} />;
    };
    (FilterComponent as any).filterFn = "arrIncludes";
    return FilterComponent;
};

//Closure
export const SelectColumnFilterArrayOfStrings = ({ column }: { column: Column<any, any, unknown>; table: Table<any, any> }): React.ReactElement => {
    const filterValue: any = column.getFilterValue();
    const [queryValue, setQueryValue] = useQueryState(column.id, filterValue || "");

    const options = React.useMemo(() => {
        const values = Array.from(column.getFacetedUniqueValues().keys());
        const uniqueValues = values.reduce((acc, value) => {
            value.forEach((x) => acc.add(x));
            return acc;
        }, new Set());
        return Array.from(uniqueValues).sort();
    }, [column]);

    column.columnDef.filterFn = "arrIncludes";

    useEffect(() => {
        if (queryValue !== filterValue && queryValue !== "") {
            column.setFilterValue(queryValue);
        }
    }, [filterValue, queryValue, column]);

    return (
        <select
            id="custom-select"
            className="custom-select"
            value={filterValue}
            onChange={(e) => {
                column.setFilterValue(e.target.value || undefined);
                setQueryValue(e.target.value || "");
            }}
        >
            <option value="">All</option>
            {options.map((optionName: string) => (
                <option key={optionName} value={optionName}>
                    {optionName}
                </option>
            ))}
        </select>
    );
};

// TanStack Table v9's built-in arrIncludes iterates the filter value as an array of needles.
// The Roles/Clients select filters pass a single string id. v9 would treat that id as characters
// ("abc" -> "a", "b", "c") and match nothing, emptying the table (e.g. SuperAdmin).
export const arrIncludes = (row, columnId, filterValue) => {
    const value = row.getValue(columnId);
    if (typeof value !== "string" && !Array.isArray(value)) {
        return false;
    }
    const needles = Array.isArray(filterValue) ? filterValue : [filterValue];
    if (Array.isArray(value)) {
        return needles.some((needle) => value.some((item) => String(item) === String(needle)));
    }
    return needles.some((needle) => value.includes(needle));
};
arrIncludes.autoRemove = (val) => val === undefined || val === null || val === "" || (Array.isArray(val) && val.length === 0);

// Expose the filterFn name so ReactTable can set it on the column def up front. Render-time
// columnDef.filterFn mutation happens after the row model is computed.
(DefaultColumnFilter as any).filterFn = "includesString";
(SelectColumnFilter as any).filterFn = "equals";
(SelectNotZeroColumnFilter as any).filterFn = "zeroFilter";
(SelectColumnFilterArrayOfStrings as any).filterFn = "arrIncludes";

export const resolveFilterFnName = (spec?: unknown): string | undefined => {
    if (typeof spec === "undefined" || spec === null) {
        return "includesString";
    }
    if (typeof spec === "function") {
        return (spec as any).filterFn;
    }
    return undefined;
};

// filterFn: notEquals
export const notEquals = (row, columnId, filterValue) => {
    const value = row.getValue(columnId);
    return value != filterValue;
};

// filterFn: includesValueFormat, use with 'valueFormat' cell
export const includesValueFormat = (row, columnId, filterValue) => {
    const value = row.getValue(columnId)?.toString();
    return value?.includes(filterValue);
};

// filterFn: numberGreaterThan, use in numeric value column
export const numberEqualOrGreaterThan = (row, columnId, filterValue) => {
    const search = Number(filterValue);
    const value = row.getValue(columnId);
    return !!(value && value >= search);
};

// filterFn: startsWith
export const startsWith = (row, columnId, filterValue) => {
    const value = row.getValue(columnId)?.toString();
    return !!(value && value.startsWith(filterValue));
};

// filterFn: notZero - filters out values that are 0 (number) or convert to 0 (string like "0.00")
export const notZero = (row, columnId, filterValue) => {
    if (!filterValue || filterValue !== "0") {
        return true; // Show all if filter is not "Exclude 0"
    }
    const value = row.getValue(columnId);
    if (value === null || value === undefined || value === "") {
        return true; // Show null/undefined/empty values
    }
    const numValue = Number(value);
    // If value cannot be converted to a number, show it (non-numeric strings, NaN, etc.)
    if (isNaN(numValue)) {
        return true;
    }
    // Only exclude if value is 0 (or converts to 0)
    return numValue !== 0;
};

// filterFn: zeroFilter - handles "All", "Exclude 0", and "Only 0" filter options
export const zeroFilter = (row, columnId, filterValue) => {
    // Show all if filter is not set
    if (!filterValue || filterValue === "") {
        return true;
    }

    const value = row.getValue(columnId);

    // Handle null/undefined/empty values
    if (value === null || value === undefined || value === "") {
        // Show null/undefined/empty when "All" or "Only 0" (since they're not zero)
        return filterValue !== "only0";
    }

    const numValue = Number(value);

    // If value cannot be converted to a number, show it for "All" and "Exclude 0", but not for "Only 0"
    if (isNaN(numValue)) {
        return filterValue !== "only0";
    }

    const isZero = numValue === 0;

    if (filterValue === "0") {
        // Exclude 0: show everything except zero
        return !isZero;
    } else if (filterValue === "only0") {
        // Only 0: show only zero values
        return isZero;
    }

    // Default: show all
    return true;
};
